import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import TextField, { type TextFieldProps } from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { TenantStatus } from '../../api/operator'
import { formatMoney, parseMoney } from '../../model/money'

/** A screen's title, what it is about, and what can be done from it. */
export function OperatorHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={2}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h1">{title}</Typography>
        {subtitle && <Typography color="text.secondary">{subtitle}</Typography>}
      </Box>
      {actions && (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {actions}
        </Stack>
      )}
    </Stack>
  )
}

const statusColours: Record<TenantStatus, 'success' | 'warning' | 'default'> = {
  active: 'success',
  offboarding: 'warning',
  offboarded: 'default',
}

export function StatusChip({ status }: { status: TenantStatus }) {
  return <Chip size="small" variant="outlined" color={statusColours[status]} label={status} />
}

/** A balance, drawn so that owing and in-credit read differently. */
export function Balance({ cents }: { cents: number }) {
  if (cents < 0) {
    return (
      <Box component="span" sx={{ color: 'success.main' }}>
        {formatMoney(-cents)} in credit
      </Box>
    )
  }
  return <Box component="span">{formatMoney(cents)}</Box>
}

/**
 * An amount field. It holds what was typed, and says so when that is not
 * an amount, rather than silently holding the last one that was.
 */
export function MoneyField({ value, onChange, ...props }: Omit<TextFieldProps, 'value' | 'onChange'> & { value: string; onChange: (value: string) => void }) {
  const invalid = value.trim() !== '' && parseMoney(value) === null
  return (
    <TextField
      {...props}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      error={invalid || props.error}
      helperText={invalid ? 'An amount, like 49.00' : props.helperText}
      slotProps={{ htmlInput: { inputMode: 'decimal' } }}
    />
  )
}

/** Cents as the text a money field starts with. */
export function centsText(cents: number | undefined): string {
  return cents === undefined ? '' : (cents / 100).toFixed(2)
}
